import React, { useState } from "react";
import { ReactComponent as ArrowUpRight } from "../../../../../assects/svg/client_details/ArrowUpRight.svg";
import GutHealthDetailPopup, { GutHealthDayLog } from "./GutHealthDetailPopup";

interface GutCategory {
  label: string;
  value: number;
}

interface GutHealthChartProps {
  data?: GutCategory[];
  vsLastWeekPercent?: number;
  popupDays?: GutHealthDayLog[];
}

const TEAL = "#20BCBB";
const AMBER = "#FFB532";
const RED = "#FF3B30";

const INSIDE_THRESHOLD = 15;

const getBarColor = (value: number): string => {
  if (value === 0) return "transparent";
  if (value <= 10) return RED;
  if (value <= 49) return AMBER;
  return TEAL;
};

const getLabelColor = (value: number): string => {
  if (value === 0) return RED;
  if (value <= 10) return RED;
  if (value <= 49) return AMBER;
  return TEAL;
};

const GutHealthChart: React.FC<GutHealthChartProps> = ({
  data = [],
  vsLastWeekPercent,
  popupDays,
}) => {
  const isUp = (vsLastWeekPercent ?? 0) >= 0;
  const [popupOpen, setPopupOpen] = useState(false);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Gut Health
        </span>
        <div className="flex items-center gap-2">
          {vsLastWeekPercent != null && (
            <>
              <span
                className={`text-[10px] 3xl:text-[12px] font-semibold px-2 py-0.5 rounded-[6px] border font-inter ${
                  isUp
                    ? "text-[#20BCBB] border-[#20BCBB] bg-[#F0FAFA]"
                    : "text-[#FF3B30] border-[#FF3B30] bg-[#FFF0F0]"
                }`}
              >
                {isUp ? "+" : ""}
                {vsLastWeekPercent}%
              </span>
              <span className="text-[10px] 3xl:text-[12px] text-[#8E8E93] font-inter">
                vs last week
              </span>
            </>
          )}
          <button
            type="button"
            onClick={() => setPopupOpen(true)}
            className="text-[#0DB5B5] hover:text-[#20BCBB] transition"
            aria-label="expand"
          >
            <ArrowUpRight className="w-4 h-4 3xl:w-5 3xl:h-5" />
          </button>
        </div>
      </div>

      {/* Rows */}
      <div className="flex flex-col gap-2.5 flex-1 justify-center">
        {data.map((item) => {
          const barColor = getBarColor(item.value);
          const labelColor = getLabelColor(item.value);
          const showInside = item.value >= INSIDE_THRESHOLD;

          return (
            <div key={item.label} className="flex items-center gap-2">
              {/* Label */}
              <span className="text-[11px] 3xl:text-[12px] text-[#646464] font-inter w-[72px] flex-shrink-0 text-left">
                {item.label}
              </span>

              {/* Bar track + label overlay */}
              <div className="flex-1 relative" style={{ height: 18 }}>
                {/* Track background */}
                <div className="absolute inset-0 bg-[#F3F4F6] rounded-[6px]" />

                {/* 0% — thin red stub */}
                {item.value === 0 && (
                  <div
                    className="absolute left-0 top-0 bottom-0 rounded-l-[24px]"
                    style={{ width: 4, background: RED }}
                  />
                )}

                {/* >0% bar */}
                {item.value > 0 && (
                  <div
                    className={`absolute left-0 top-0 bottom-0 transition-all duration-700 ${
                      item.value < INSIDE_THRESHOLD
                        ? "rounded-l-[6px]"
                        : "rounded-[6px]"
                    }`}
                    style={{
                      width: `${item.value}%`,
                      minWidth: 6,
                      background: barColor,
                    }}
                  />
                )}

                {/* Label INSIDE bar (≥ threshold) */}
                {showInside && item.value > 0 && (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 font-semibold font-inter leading-none pointer-events-none"
                    style={{ fontSize: 9, right: `calc(${100 - item.value}% + 6px)`, color: "#1F1F1F" }}
                  >
                    {item.value}%
                  </span>
                )}

                {/* Label OUTSIDE bar (< threshold or 0%) */}
                {(!showInside || item.value === 0) && (
                  <span
                    className="absolute top-1/2 -translate-y-1/2 font-semibold font-inter leading-none whitespace-nowrap pointer-events-none"
                    style={{
                      fontSize: 9,
                      left: item.value === 0 ? 7 : `max(${item.value}%, 5px)`,
                      marginLeft: item.value === 0 ? 0 : 5,
                      color: labelColor,
                    }}
                  >
                    {item.value}%
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail popup */}
      <GutHealthDetailPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        days={popupDays}
      />
    </div>
  );
};

export default GutHealthChart;